import { useCallback, useEffect, useRef, useState } from "react";

import { isLoggedIn } from "services/AccountServices";

class SessionManager {
    private static instance: SessionManager;
    private isInitialized: boolean = false;
    private isSessionActive: boolean = true;
    private activeTimeoutHandlers: Set<string> = new Set();
    private removeTimeoutHandlerFunctions: Map<string, () => void> = new Map();
    private timeoutInProgress: boolean = false;
    private subscribers: ((isActive: boolean) => void)[] = [];

    private sessionExemptEndpoints: string[] = [
        "/diagnostics/",
        "/login/",
        "/logout/",
        "/password/change/",
        "/password/reset/",
        "/password/reset/confirm/",
        "/verify-email/",
        "/version/"
    ];

    private constructor() {}

    static getInstance(): SessionManager {
        if (!SessionManager.instance) {
            SessionManager.instance = new SessionManager();
        }
        return SessionManager.instance;
    }

    subscribe(callback: (isActive: boolean) => void) {
        this.subscribers.push(callback);
        return () => {
            this.subscribers = this.subscribers.filter(sub => sub !== callback);
        };
    }

    private notifySubscribers() {
        this.subscribers.forEach(callback => callback(this.isSessionActive));
    }

    private ensureInitialized() {
        if (!this.isInitialized && isLoggedIn() && this.isSessionActive && !this.isTimeoutInProgress) {
            this.initialize();
        }
    }

    initialize() {
        if (this.isInitialized) {
            console.warn("[SessionManager] Already initialized. Skipping.");
            return;
        }

        this.isInitialized = true;
        this.isSessionActive = true;
        this.timeoutInProgress = false;
        console.log("[SessionManager] Session initialized");
        this.notifySubscribers();
    }

    destroy() {
        if (this.isInitialized) {
            console.log("[SessionManager] Session destroyed");

            // Immediately remove timeout handlers
            this.removeTimeoutHandlerFunctions.forEach((removeFn, componentName) => {
                console.log(`${componentName} Removing timeout handler`);
                removeFn();
            });
        }
        this.isInitialized = false;
        this.isSessionActive = true;
        this.timeoutInProgress = false;
        this.activeTimeoutHandlers.clear();
        this.removeTimeoutHandlerFunctions.clear();
    }

    registerTimeoutHandler(componentName: string, removeFn: () => void): boolean {
        this.ensureInitialized();

        if (this.activeTimeoutHandlers.has(componentName)) {
            console.warn(`${componentName} Timeout handler already exists`);
            return false;
        }

        this.activeTimeoutHandlers.add(componentName);
        this.removeTimeoutHandlerFunctions.set(componentName, removeFn);
        console.log(`${componentName} Registered timeout handler. Active handlers: ${this.activeTimeoutHandlers.size}`);
        return true;
    }

    unregisterTimeoutHandler(componentName: string): void {
        if (this.activeTimeoutHandlers.has(componentName)) {
            this.activeTimeoutHandlers.delete(componentName);
            this.removeTimeoutHandlerFunctions.delete(componentName);
            console.log(`${componentName} Unregistered timeout handler. Active handlers: ${this.activeTimeoutHandlers.size}`);
        }
        else {
            console.log(`${componentName} Handler not found for unregistration (already removed)`);
        }
    }

    markSessionTimeout() {
        console.log("[SessionManager] Session timeout triggered");
        this.isSessionActive = false;
        this.timeoutInProgress = true;
        this.notifySubscribers();
    }

    resetSession() {
        console.log("[SessionManager] Session reset");
        this.isSessionActive = true;
        this.timeoutInProgress = false;
        this.notifySubscribers();
    }

    isActive(): boolean {
        this.ensureInitialized();
        return this.isSessionActive;
    }

    isTimeoutInProgress(): boolean {
        return this.timeoutInProgress;
    }

    isSessionInitialized(): boolean {
        return this.isInitialized;
    }

    getActiveTimeoutHandlers(): number {
        return this.activeTimeoutHandlers.size;
    }

    canMakeApiCall(url?: string): boolean {
        if (!url) {
            this.ensureInitialized();
            return this.isInitialized && this.isSessionActive && !this.timeoutInProgress;
        }

        const isExemptEndpoint = this.sessionExemptEndpoints.some(endpoint =>
            url.includes(endpoint)
        );

        if (isExemptEndpoint) {
            return true;
        }

        this.ensureInitialized();
        return this.isInitialized && this.isSessionActive && !this.timeoutInProgress;
    }
}

export interface UseSessionTimeoutOptions {
    timeout: number;
    onTimeout: (userMessage: string, logMessage: string) => void;
    parentComponentName?: string;
    enabled?: boolean;
}

export const useSessionTimeout = ({
    timeout,
    onTimeout,
    parentComponentName = null,
    enabled = true
}: UseSessionTimeoutOptions) => {
    const componentName = `[SessionManager]${parentComponentName || ""}`;

    const [sessionIdleTime, setSessionIdleTime] = useState(0);
    const timeoutRef = useRef<number | undefined>(undefined);
    const hasTimedOut = useRef(false);
    const isRegistered = useRef(false);

    const events = ["mousemove", "mousedown", "click", "keydown", "touchstart", "scroll"];

    const resetTimer = useCallback(
        () => {
            setSessionIdleTime(0);
            hasTimedOut.current = false;
            if (!sessionManager.isActive()) {
                sessionManager.resetSession();
            }
        }, []
    );

    const formatSeconds = useCallback((seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        if (minutes > 0) {
            return `${minutes}m ${remainingSeconds}s`;
        }
        return `${remainingSeconds}s`;
    }, []);

    const removeTimeoutHandler = useCallback(() => {
        if (!isRegistered.current) {
            return;
        }

        events.forEach(event => {
            document.removeEventListener(event, resetTimer, true);
        });
        window.removeEventListener("load", resetTimer);

        if (timeoutRef.current !== undefined) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = undefined;
        }

        console.log(`${componentName} Removed timeout event listeners`);

        sessionManager.unregisterTimeoutHandler(componentName);
        isRegistered.current = false;

    }, [resetTimer, componentName]);

    useEffect(
        () => {
            if (!enabled) {
                removeTimeoutHandler();
                return;
            }

            // Only register if we haven't already and if registration succeeds
            if (!isRegistered.current && sessionManager.registerTimeoutHandler(componentName, removeTimeoutHandler)) {
                isRegistered.current = true;

                events.forEach(event => {
                    document.addEventListener(event, resetTimer, true);
                });

                window.addEventListener("load", resetTimer);
                console.log(`${componentName} Added timeout event listeners`);

                return () => {
                    removeTimeoutHandler();
                };
            }
        }, [resetTimer, enabled, componentName, removeTimeoutHandler]
    );

    useEffect(
        () => {
            if (!enabled || hasTimedOut.current || !isRegistered.current) {
                return;
            }

            // Only allow the first registered handler to manage the timeout
            if (sessionManager.getActiveTimeoutHandlers() > 1) {
                console.warn(`${componentName} Multiple timeout handlers detected (${sessionManager.getActiveTimeoutHandlers()}), skipping timeout management`);
                return;
            }

            timeoutRef.current = window.setTimeout(() => {
                if (sessionIdleTime % 60 === 0) {
                    let timeRemaining = timeout - sessionIdleTime;
                    timeRemaining = timeRemaining < 0 ? 0 : timeRemaining;
                    let formattedTimeLeft = formatSeconds(timeRemaining);
                    console.log(`${componentName} Timeout trigger in ${formattedTimeLeft}`);
                }

                if (sessionIdleTime >= timeout) {
                    hasTimedOut.current = true;

                    if (timeoutRef.current !== undefined) {
                        window.clearTimeout(timeoutRef.current);
                        timeoutRef.current = undefined;
                    }

                    sessionManager.markSessionTimeout();

                    let formattedTimeOut = formatSeconds(timeout);
                    let timeoutMessage = `Timed out after ${formattedTimeOut}`;
                    console.log(`${componentName} ${timeoutMessage}`);
                    onTimeout("Your session timed out. Please login again.", timeoutMessage);
                }
                else {
                    setSessionIdleTime(prev => prev + 1);
                }
            }, 1000);

            return () => {
                if (timeoutRef.current !== undefined) {
                    window.clearTimeout(timeoutRef.current);
                    timeoutRef.current = undefined;
                }
            };
        }, [sessionIdleTime, timeout, onTimeout, componentName, formatSeconds, enabled]
    );

    return { sessionIdleTime, resetTimer };
};

export const useSessionState = () => {
    return {
        isSessionActive: () => sessionManager.isActive(),
        isSessionInitialized: () => sessionManager.isSessionInitialized(),
        canMakeApiCall: (url?: string) => sessionManager.canMakeApiCall(url),
        resetSession: () => sessionManager.resetSession()
    };
};

export const sessionManager = SessionManager.getInstance();